/* eslint-disable no-undef */
import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Card,
  CardContent,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { SEO } from '../../components/SEO/SEO';
import { useQuery } from '@tanstack/react-query';
import { useCurrentUser } from '../../hooks/useApi';
import { useSingleMarketAccess } from '../../hooks/useMarketAccess';
import { useMarketDetails } from '../../hooks/useMarketCompassApi';
import { usePublicFetch } from '../../hooks/usePublicFetch';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { useUserEntitlements } from '../../hooks/useUserEntitlements';
import { API_URL } from '../../utils/config';
import { PodcastPlayer } from '../../components/podcasts/PodcastPlayer';
import HotelIcon from '@mui/icons-material/Hotel';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import DescriptionIcon from '@mui/icons-material/Description';
import HeadphonesIcon from '@mui/icons-material/Headphones';
import SettingsIcon from '@mui/icons-material/Settings';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { MarketPerformanceDashboard } from '../../components/markets/analytics/MarketPerformanceDashboard';
import { brandColors } from '../../theme';
import { AppBreadcrumbs } from '../../components/navigation/AppBreadcrumbs';

const MarketPage: React.FC = () => {
  const { marketId } = useParams<{ marketId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: currentUser, isLoading: isLoadingUser } = useCurrentUser();
  const [selectedPeriod, setSelectedPeriod] = React.useState<number | null>(null);
  const { publicFetch } = usePublicFetch();
  const { authenticatedFetch } = useAuthenticatedFetch();
  
  // Convert marketId to number for the access check
  const marketIdNum = React.useMemo(() => {
    return marketId ? parseInt(marketId, 10) : undefined;
  }, [marketId]);
  
  // Memoize the enabled flag
  const isEnabled = React.useMemo(() => {
    return !!currentUser && !!marketIdNum && !isNaN(marketIdNum);
  }, [currentUser, marketIdNum]);
  
  // Check if user has access to this market
  const { hasAccess, loading, error } = useSingleMarketAccess(marketIdNum, {
    enabled: isEnabled,
  });

  // Fetch market details (must be called before any conditional returns)
  const { data: marketDetails, isLoading: isLoadingDetails, error: detailsError } = useMarketDetails(marketId);

  // Memoize filters to prevent infinite re-renders
  const entitlementFilters = React.useMemo(() => ({}), []);

  // Fetch user entitlements to check for customization license
  const { data: entitlementsData } = useUserEntitlements({
    filters: entitlementFilters,
    enabled: !!currentUser && !!marketIdNum,
  });

  // State to track market groupings that include this market
  const [groupingMarketsMap, setGroupingMarketsMap] = React.useState<Map<number, number[]>>(new Map());

  // Fetch market grouping data for grouping customization entitlements
  React.useEffect(() => {
    if (!entitlementsData?.entitlements || !marketIdNum || !currentUser) return;

    const groupingEntitlements = entitlementsData.entitlements.filter(e =>
      e.accessLevel === 'market_grouping' &&
      e.productSubtype === 'grouping_customization_license' &&
      ['active', 'expiring'].includes(e.status)
    );

    if (groupingEntitlements.length === 0) {
      setGroupingMarketsMap(new Map());
      return;
    }

    const fetchGroupingMarkets = async () => {
      const newMap = new Map<number, number[]>();

      for (const entitlement of groupingEntitlements) {
        if (!entitlement.marketGroupingId) continue;

        try {
          const response = await authenticatedFetch(
            `${API_URL}/api/store/market-groupings/${entitlement.marketGroupingId}`
          );

          if (response?.ok) {
            const data = await response.json();
            if (data.grouping?.markets) {
              const marketIds = data.grouping.markets.map((m: any) => m.id);
              newMap.set(entitlement.marketGroupingId, marketIds);
            }
          }
        } catch (error) {
          console.error('Error fetching market grouping:', error);
        }
      }

      setGroupingMarketsMap(newMap);
    };

    fetchGroupingMarkets();
  }, [entitlementsData, marketIdNum, currentUser, authenticatedFetch]);

  // Check if user has a customization license for this market
  const hasCustomizationLicense = React.useMemo(() => {
    if (!entitlementsData?.entitlements || !marketIdNum) return false;

    // Check for direct market customization license
    const hasDirectLicense = entitlementsData.entitlements.some(e =>
      e.accessLevel === 'market' &&
      e.market?.id === marketIdNum &&
      e.productSubtype === 'customization_license' &&
      ['active', 'expiring'].includes(e.status)
    );

    if (hasDirectLicense) return true;

    // Check for market grouping customization license
    const hasGroupingLicense = Array.from(groupingMarketsMap.values()).some(marketIds =>
      marketIds.includes(marketIdNum)
    );

    return hasGroupingLicense;
  }, [entitlementsData, marketIdNum, groupingMarketsMap]);

  // Fetch market contacts - public endpoint
  const { data: marketContacts } = useQuery({
    queryKey: ['market-contacts', marketIdNum],
    queryFn: async () => {
      if (!marketIdNum) return null;

      const response = await publicFetch(`${API_URL}/api/store/markets/${marketIdNum}/contacts`);

      if (!response || !response.ok) {
        console.error('Failed to fetch market contacts:', response?.status);
        return null;
      }

      const result = await response.json();
      return result.data;
    },
    enabled: !!marketIdNum,
    staleTime: 15 * 60 * 1000, // 15 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });

  // Fetch reports for period dropdown
  const { data: reportsData } = useQuery({
    queryKey: ['market-reports', marketIdNum],
    queryFn: async () => {
      if (!marketIdNum || !authenticatedFetch) return null;

      const response = await authenticatedFetch(`${API_URL}/api/marketcompass/markets/${marketIdNum}/reports`);

      if (!response || !response.ok) {
        console.error('Failed to fetch reports:', response?.status);
        return null;
      }

      const result = await response.json();
      return result; // API returns { marketId, reports } directly
    },
    enabled: !!marketIdNum && !!authenticatedFetch,
  });

  // Set default selected period to most recent report
  React.useEffect(() => {
    if (reportsData?.reports && reportsData.reports.length > 0 && !selectedPeriod) {
      setSelectedPeriod(reportsData.reports[0].reportMonth);
    }
  }, [reportsData, selectedPeriod]);

  // Fetch podcasts for selected period
  const { data: podcastData } = useQuery({
    queryKey: ['market-podcasts', marketIdNum],
    queryFn: async () => {
      if (!marketIdNum || !authenticatedFetch) return null;

      const response = await authenticatedFetch(`${API_URL}/api/marketcompass/markets/${marketIdNum}/podcasts`);

      if (!response || !response.ok) {
        console.error('Failed to fetch podcasts:', response?.status);
        return null;
      }

      const result = await response.json();
      return result; // API returns { marketId, podcasts } directly
    },
    enabled: !!marketIdNum && !!authenticatedFetch,
  });

  // Fetch key findings for selected period
  const { data: reportKeyFindings } = useQuery({
    queryKey: ['report-key-findings', marketIdNum, selectedPeriod],
    queryFn: async () => {
      if (!marketIdNum || !selectedPeriod || !authenticatedFetch) return null;

      const selectedReport = reportsData?.reports?.find(
        (report: any) => report.reportMonth === selectedPeriod
      );

      if (!selectedReport) return null;

      const response = await authenticatedFetch(
        `${API_URL}/api/marketcompass/reports/${selectedReport.reportId}/key-findings`
      );

      if (!response || !response.ok) {
        console.error('Failed to fetch report key findings:', response?.status);
        return null;
      }

      const result = await response.json();
      return result; // API returns the response directly
    },
    enabled: !!marketIdNum && !!selectedPeriod && !!reportsData?.reports && !!authenticatedFetch,
  });

  // Handle redirect to login if user is not authenticated
  React.useEffect(() => {
    if (!currentUser && !isLoadingUser) {
      navigate('/login', { state: { from: location.pathname + location.search } });
    }
  }, [currentUser, isLoadingUser, navigate, location.pathname, location.search]);

  // If user is still loading
  if (isLoadingUser) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 3, textAlign: 'center' }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Loading user data...</Typography>
        </Box>
      </Container>
    );
  }
  
  // If user is not authenticated (currentUser is null and not loading)
  if (!currentUser && !isLoadingUser) {
    // This shouldn't happen as ProtectedRoute should handle this, but just in case
    // Navigation is handled by useEffect above
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 3, textAlign: 'center' }}>
          <Typography sx={{ mt: 2 }}>Redirecting to login...</Typography>
        </Box>
      </Container>
    );
  }
  
  // If currentUser exists but might be empty or invalid
  if (!currentUser) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 3 }}>
          <Alert severity="error">
            Unable to load user information. Please try refreshing the page.
          </Alert>
          <Box sx={{ mt: 2 }}>
            <Button variant="contained" onClick={() => window.location.reload()}>
              Refresh Page
            </Button>
          </Box>
        </Box>
      </Container>
    );
  }

  // If market ID is invalid
  if (!marketIdNum || isNaN(marketIdNum)) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 3 }}>
          <Alert severity="error">
            Invalid market ID provided.
          </Alert>
        </Box>
      </Container>
    );
  }

  // If still checking access
  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 3, textAlign: 'center' }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Checking market access...</Typography>
        </Box>
      </Container>
    );
  }

  // If there was an error checking access
  if (error) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 3 }}>
          <Alert severity="error">
            Error checking market access. Please try again later.
          </Alert>
        </Box>
      </Container>
    );
  }

  // If user doesn't have access
  if (!hasAccess) {
    return (
      <Container maxWidth="md">
        <Box sx={{ py: 6 }}>
          <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h5" gutterBottom color="error">
              Access Denied
            </Typography>
            
            <Typography variant="body1" sx={{ mb: 3 }}>
              You don't have an active subscription to this market.
            </Typography>
            
            <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
              To access this market's data and insights, you need an active subscription.
            </Alert>
            
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                color="primary"
                component={Link}
                to={`/global-market-reports?marketId=${marketId}`}
                sx={{ minWidth: 200 }}
              >
                View in Market Store
              </Button>
              
              <Button
                variant="outlined"
                onClick={() => navigate('/subscriptions')}
              >
                My Subscriptions
              </Button>
            </Box>
          </Paper>
        </Box>
      </Container>
    );
  }

  // User has access - show market content
  
  // Final check - if we've gotten here, we should render something
  if (!hasAccess && !loading && !error) {
    console.error('MarketPage: Unexpected state - no access, no loading, no error');
  }
  
  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 3 }}>
        {/* Loading state for market details */}
        {isLoadingDetails && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>Loading market details...</Typography>
          </Box>
        )}

        {/* Error state for market details */}
        {detailsError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load market details. Please try again.
          </Alert>
        )}

        {/* Market Details Display */}
        {marketDetails && (
          <>
            {/* Page Title */}
            <SEO
              title={`${marketDetails.name} - My Markets`}
              description="View and manage your subscribed hotel markets. Access detailed market reports, trend analysis, and competitive insights for your active market subscriptions."
              robots="noindex, follow"
            />

            {/* Market Header */}
            <Box sx={{ mb: 4 }}>
              <Typography
                component="h1"
                sx={{
                  color: brandColors.blue,
                  fontWeight: 400,
                  fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' },
                  lineHeight: 1.2,
                  letterSpacing: '-0.5px',
                  mb: 1,
                }}
              >
                {marketDetails.name}
              </Typography>
              <Typography
                component="h2"
                sx={{
                  color: brandColors.blue,
                  fontWeight: 300,
                  fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                  lineHeight: 1.2,
                  letterSpacing: '-0.3px',
                }}
              >
                MarketCompass Report Subscription
              </Typography>
            </Box>

            {/* Breadcrumbs */}
            <AppBreadcrumbs
              items={[
                { label: 'My Subscriptions', path: '/subscriptions' },
                { label: marketDetails.name, path: `/market/${marketId}` },
              ]}
              sx={{ mb: 4 }}
            />

            {/* Customization License Info Box */}
            {hasCustomizationLicense && (
              <Alert
                severity="info"
                icon={<SettingsIcon />}
                sx={{
                  mb: 4,
                  backgroundColor: '#F5A80010',
                  borderLeft: '4px solid #F5A800',
                  '& .MuiAlert-icon': {
                    color: '#F5A800',
                  },
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  You have a Customization License for {marketDetails.name}
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  To customize what appears on the HTL MarketCompass website and in the monthly MarketCompass generated reports,
                  please login to the Horwath HTL US Portal where you'll have access to all sorts of tools to customize this market's content.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<OpenInNewIcon />}
                  href={`https://portal.horwathhtlus.com/admin/market/${marketIdNum}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    backgroundColor: '#F5A800',
                    color: '#FFFFFF',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: '#E09800',
                    },
                  }}
                >
                  Open Market Customization Portal
                </Button>
              </Alert>
            )}

            {/* Market Overview Section */}
            <Paper sx={{ p: { xs: 2, sm: 3, md: 4 }, mb: 4 }}>
              <Grid container spacing={3}>
                {/* Left Column - Product Image and Market Info */}
                <Grid item xs={12} md={marketContacts && marketContacts.length > 0 ? 8 : 12}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', md: 'row' },
                      gap: { xs: 2, md: 4 },
                    }}
                  >
                    {/* Product Image Section */}
                    <Box
                      sx={{
                        flexShrink: 0,
                        width: { xs: '100%', md: 'auto' },
                        maxWidth: '400px',
                        mx: { xs: 'auto', md: 0 },
                      }}
                    >
                      {marketDetails.productImage ? (
                        <Box
                          sx={{
                            width: '100%',
                            maxWidth: '400px',
                            aspectRatio: '4/3',
                            position: 'relative',
                            overflow: 'hidden',
                            borderRadius: 2,
                            backgroundColor: '#f5f5f5',
                            border: '1px solid #e0e0e0',
                          }}
                        >
                          <img
                            src={marketDetails.productImage}
                            alt={marketDetails.name}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain',
                              display: 'block',
                            }}
                            onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                              const target = e.currentTarget;
                              target.style.display = 'none';
                              const parent = target.parentElement;
                              if (parent) {
                                parent.innerHTML = '<div style="display: flex; align-items: center; justify-content: center; height: 100%; font-size: 48px;">🏨</div>';
                              }
                            }}
                          />
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            width: '100%',
                            maxWidth: '400px',
                            aspectRatio: '4/3',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#f5f5f5',
                            borderRadius: 2,
                            border: '1px solid #e0e0e0',
                          }}
                        >
                          <HotelIcon sx={{ fontSize: 64, color: 'text.secondary' }} />
                        </Box>
                      )}
                    </Box>

                    {/* Market Information Section */}
                    <Box sx={{ flex: 1 }}>
                      {/* Location Details */}
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" gutterBottom>
                          Location Details
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                          <strong>Country:</strong> {marketDetails.country.name} ({marketDetails.country.code})
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                          <strong>Continent:</strong> {marketDetails.continent.name}
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                          <strong>Market ID:</strong> {marketDetails.id}
                        </Typography>
                      </Box>

                      <Divider sx={{ my: 3 }} />

                      {/* Subscription Information */}
                      {marketDetails.subscription && (
                        <>
                          <Box sx={{ mb: 3 }}>
                            <Typography variant="h6" gutterBottom>
                              Subscription Details
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 1 }}>
                              <strong>Product:</strong> {marketDetails.subscription.productName}
                            </Typography>
                            {marketDetails.subscription.description && (
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {marketDetails.subscription.description}
                              </Typography>
                            )}
                            {marketDetails.subscription.prices && marketDetails.subscription.prices.length > 0 && (
                              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                {marketDetails.subscription.prices.map((price) => (
                                  <Chip
                                    key={price.priceId}
                                    label={`${price.currency.toUpperCase()} ${price.amount / 100}/${price.interval}`}
                                    size="small"
                                    variant="outlined"
                                  />
                                ))}
                              </Box>
                            )}
                          </Box>
                        </>
                      )}
                    </Box>
                  </Box>
                </Grid>

                {/* Right Column - Market Representatives */}
                {marketContacts && marketContacts.length > 0 && (
                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider', height: '100%' }}>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                        Market Representatives
                      </Typography>
                      {marketContacts.map((contact: any, index: number) => {
                        return (
                          <Box
                            key={contact.id}
                            sx={{
                              mb: index < marketContacts.length - 1 ? 3 : 0,
                              display: 'flex',
                              gap: 2
                            }}
                          >
                            {/* Headshot or Placeholder */}
                            {contact.headshotIconUrl ? (
                              <Box
                                component="img"
                                src={contact.headshotIconUrl}
                                alt={contact.name}
                                sx={{
                                  width: 80,
                                  height: 80,
                                  minWidth: 80,
                                  borderRadius: '50%',
                                  objectFit: 'cover',
                                  border: '2px solid',
                                  borderColor: 'divider'
                                }}
                              />
                            ) : (
                              <Box
                                sx={{
                                  width: 80,
                                  height: 80,
                                  minWidth: 80,
                                  borderRadius: '50%',
                                  bgcolor: 'white',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  border: '2px solid',
                                  borderColor: 'divider',
                                  overflow: 'hidden',
                                  p: 1
                                }}
                              >
                                <Box
                                  component="img"
                                  src="/images/horwath_mountain.jpg"
                                  alt="Horwath HTL"
                                  sx={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain'
                                  }}
                                />
                              </Box>
                            )}

                            {/* Contact Details */}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              {/* Name and Title */}
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                                {contact.name}
                                {contact.credentials && (
                                  <Typography component="span" variant="body2" color="text.secondary">
                                    , {contact.credentials}
                                  </Typography>
                                )}
                              </Typography>

                              {contact.title && (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                  {contact.title}
                                </Typography>
                              )}

                              {/* Contact Information */}
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                {contact.email && (
                                  <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                    <a href={`mailto:${contact.email}`} style={{ color: brandColors.blue, textDecoration: 'none' }}>
                                      {contact.email}
                                    </a>
                                  </Typography>
                                )}
                                {contact.office && (
                                  <Typography variant="body2" color="text.secondary">
                                    Office: {contact.office}
                                  </Typography>
                                )}
                                {contact.mobile && (
                                  <Typography variant="body2" color="text.secondary">
                                    Mobile: {contact.mobile}
                                  </Typography>
                                )}
                              </Box>

                              {/* Horwath HTL Profile Link */}
                              {contact.horwathhtlUrl && (
                                <Box sx={{ mt: 1 }}>
                                  <a
                                    href={contact.horwathhtlUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      color: brandColors.blue,
                                      textDecoration: 'none',
                                      fontSize: '0.875rem'
                                    }}
                                  >
                                    View Profile →
                                  </a>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        );
                      })}
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </Paper>

            {/* Current Insights */}
            {marketDetails.description && (
              <Card sx={{ mb: 4 }}>
                <CardContent sx={{ position: 'relative' }}>
                  <Typography variant="h4" sx={{ fontWeight: 600, mb: 3 }}>
                    Current Insights
                  </Typography>

                  {/* Period Dropdown - Positioned absolutely on desktop */}
                  {reportsData?.reports && reportsData.reports.length > 0 && (
                    <Box
                      sx={{
                        position: { xs: 'static', md: 'absolute' },
                        top: { md: 24 },
                        right: { md: 12 },
                        width: { xs: '100%', md: 'calc(33.333% - 12px)' }, // 4/12 columns with proper alignment
                        display: { xs: 'none', md: 'block' },
                      }}
                    >
                      <FormControl size="small" fullWidth>
                        <InputLabel
                          id="period-select-label"
                          sx={{
                            color: 'text.secondary',
                            '&.Mui-focused': {
                              color: 'primary.main',
                            }
                          }}
                        >
                          Report Period
                        </InputLabel>
                        <Select
                          labelId="period-select-label"
                          id="period-select"
                          value={selectedPeriod || ''}
                          label="Report Period"
                          onChange={(e) => setSelectedPeriod(e.target.value as number)}
                          sx={{
                            backgroundColor: 'white',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'divider',
                              borderWidth: '1px',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'divider',
                              borderWidth: '1px',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'primary.main',
                              borderWidth: '1px',
                            },
                            '& .MuiSelect-select': {
                              fontWeight: 600,
                              color: 'text.primary',
                            },
                            '& .MuiSvgIcon-root': {
                              color: 'action.active',
                            }
                          }}
                        >
                          {reportsData.reports.map((report: any) => {
                            const year = Math.floor(report.reportMonth / 100);
                            const month = report.reportMonth % 100;
                            const date = new Date(year, month - 1);
                            const periodLabel = date.toLocaleDateString('en-US', {
                              month: 'long',
                              year: 'numeric'
                            });

                            return (
                              <MenuItem
                                key={report.reportMonth}
                                value={report.reportMonth}
                                sx={{
                                  '&:hover': {
                                    backgroundColor: '#F5A80020',
                                  },
                                  '&.Mui-selected': {
                                    backgroundColor: '#F5A80030',
                                    '&:hover': {
                                      backgroundColor: '#F5A80040',
                                    }
                                  }
                                }}
                              >
                                {periodLabel}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    </Box>
                  )}

                  {/* Mobile Period Dropdown - Shown only on mobile */}
                  {reportsData?.reports && reportsData.reports.length > 0 && (
                    <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 3 }}>
                      <FormControl
                        size="small"
                        fullWidth
                      >
                        <InputLabel
                          id="period-select-label-mobile"
                          sx={{
                            color: 'text.secondary',
                            '&.Mui-focused': {
                              color: 'primary.main',
                            }
                          }}
                        >
                          Report Period
                        </InputLabel>
                        <Select
                          labelId="period-select-label-mobile"
                          id="period-select-mobile"
                          value={selectedPeriod || ''}
                          label="Report Period"
                          onChange={(e) => setSelectedPeriod(e.target.value as number)}
                          sx={{
                            backgroundColor: 'white',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'divider',
                              borderWidth: '1px',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'divider',
                              borderWidth: '1px',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'primary.main',
                              borderWidth: '1px',
                            },
                            '& .MuiSelect-select': {
                              fontWeight: 600,
                              color: 'text.primary',
                            },
                            '& .MuiSvgIcon-root': {
                              color: 'action.active',
                            }
                          }}
                        >
                          {reportsData.reports.map((report: any) => {
                            const year = Math.floor(report.reportMonth / 100);
                            const month = report.reportMonth % 100;
                            const date = new Date(year, month - 1);
                            const periodLabel = date.toLocaleDateString('en-US', {
                              month: 'long',
                              year: 'numeric'
                            });

                            return (
                              <MenuItem
                                key={report.reportMonth}
                                value={report.reportMonth}
                                sx={{
                                  '&:hover': {
                                    backgroundColor: '#F5A80020',
                                  },
                                  '&.Mui-selected': {
                                    backgroundColor: '#F5A80030',
                                    '&:hover': {
                                      backgroundColor: '#F5A80040',
                                    }
                                  }
                                }}
                              >
                                {periodLabel}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    </Box>
                  )}

                  <Grid container spacing={3}>
                    {/* Main Content Column */}
                    <Grid item xs={12} md={8}>
                      <Typography variant="body1" color="text.secondary" sx={{ whiteSpace: 'pre-line', mb: 3 }}>
                        {marketDetails.description}
                      </Typography>
                    </Grid>

                    {/* Right Sidebar Column */}
                    <Grid item xs={12} md={4}>
                      <Paper sx={{
                        p: 2,
                        bgcolor: 'grey.50',
                        border: '1px solid',
                        borderColor: 'divider'
                      }}>
                        {/* Report in Development Message */}
                        {(!reportsData?.reports || reportsData.reports.length === 0) && (
                          <Alert
                            severity="info"
                            icon={<LightbulbIcon />}
                            sx={{
                              backgroundColor: '#F5A80010',
                              borderLeft: '4px solid #F5A800',
                              '& .MuiAlert-icon': {
                                color: '#F5A800',
                              },
                              '& .MuiAlert-message': {
                                width: '100%',
                              }
                            }}
                          >
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                              Your Market Report is Being Prepared
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              Our MarketCompass experts are currently developing your comprehensive market report. It will be available within 1 business day.
                            </Typography>
                            <Typography variant="body2">
                              You will receive an email notification as soon as your report is ready to download.
                            </Typography>
                          </Alert>
                        )}

                        {/* Download Report Button */}
                        {selectedPeriod && reportsData?.reports && (
                          <Box sx={{ mb: 2 }}>
                            <Button
                              variant="contained"
                              fullWidth
                              startIcon={<DescriptionIcon />}
                              sx={{
                                backgroundColor: '#F5A800',
                                color: '#FFFFFF',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                padding: '0.75em 1.5em',
                                borderRadius: '3px',
                                '&:hover': {
                                  backgroundColor: '#E09800',
                                  transform: 'translateY(-2px)',
                                  boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                                },
                                transition: 'all 0.2s ease',
                                '& .MuiSvgIcon-root': {
                                  color: '#FFFFFF',
                                }
                              }}
                              onClick={async () => {
                                const selectedReport = reportsData.reports.find(
                                  (report: any) => report.reportMonth === selectedPeriod
                                );
                                if (selectedReport) {
                                  const year = Math.floor(selectedPeriod / 100);
                                  const month = selectedPeriod % 100;
                                  const date = new Date(year, month - 1);
                                  const periodLabel = date.toLocaleDateString('en-US', {
                                    month: 'long',
                                    year: 'numeric'
                                  });

                                  try {
                                    // First, get the download URL from the API
                                    const response = await authenticatedFetch(
                                      `${API_URL}/api/marketcompass/markets/${marketIdNum}/reports/${selectedReport.reportId}/download-url`
                                    );

                                    if (response && response.ok) {
                                      const data = await response.json();

                                      // Direct download using the SAS URL (avoids CORS issues)
                                      if (data.downloadUrl) {
                                        const a = document.createElement('a');
                                        a.href = data.downloadUrl;
                                        a.download = data.fileName || `${marketDetails.name} - ${periodLabel}.pdf`;
                                        a.target = '_blank'; // Open in new tab as fallback
                                        document.body.appendChild(a);
                                        a.click();
                                        document.body.removeChild(a);
                                      }
                                    } else {
                                      console.error('Error getting download URL:', response?.statusText);
                                    }
                                  } catch (error) {
                                    console.error('Error downloading report:', error);
                                  }
                                }
                              }}
                            >
                              Download{' '}
                              {(() => {
                                const year = Math.floor(selectedPeriod / 100);
                                const month = selectedPeriod % 100;
                                const date = new Date(year, month - 1);
                                return date.toLocaleDateString('en-US', {
                                  month: 'long',
                                  year: 'numeric'
                                });
                              })()} Report
                            </Button>
                          </Box>
                        )}

                        {/* Podcast Audio Player */}
                        {selectedPeriod && podcastData?.podcasts && (() => {
                          const selectedPodcast = podcastData.podcasts.find(
                            (podcast: any) => podcast.reportMonth === selectedPeriod
                          );

                          if (selectedPodcast && marketIdNum) {
                            return (
                              <Box sx={{ mt: 2 }}>
                                <PodcastPlayer podcast={selectedPodcast} />
                              </Box>
                            );
                          }
                          return null;
                        })()}
                      </Paper>
                    </Grid>
                  </Grid>

                  {/* Key Findings Section - Full Width */}
                  {reportKeyFindings?.findings && reportKeyFindings.findings.length > 0 && (
                    <Box sx={{ mt: 4 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <LightbulbIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
                          Key Findings
                        </Typography>
                      </Box>
                      <Grid container spacing={2}>
                        {reportKeyFindings.findings.map((finding: any, index: number) => (
                          <Grid item xs={12} sm={6} md={4} key={finding.id || index}>
                            <Paper
                              sx={{
                                p: 2,
                                bgcolor: 'background.paper',
                                border: '1px solid',
                                borderColor: 'divider',
                                height: '100%',
                                minHeight: 200,
                                display: 'flex',
                                flexDirection: 'column',
                                '&:hover': {
                                  boxShadow: 2,
                                  borderColor: 'primary.light',
                                },
                                transition: 'all 0.2s ease'
                              }}
                            >
                              {finding.findingTitle && (
                                <Box sx={{ mb: 1.5 }}>
                                  {finding.findingNumber && (
                                    <Chip
                                      label={`Finding ${finding.findingNumber}`}
                                      size="small"
                                      sx={{
                                        bgcolor: 'primary.main',
                                        color: 'white',
                                        fontWeight: 600,
                                        height: 20,
                                        fontSize: '0.75rem',
                                        mb: 1
                                      }}
                                    />
                                  )}
                                  <Typography
                                    variant="subtitle2"
                                    component="h4"
                                    sx={{
                                      fontWeight: 600,
                                      color: 'primary.main',
                                      lineHeight: 1.3,
                                      minHeight: 40,
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                      overflow: 'hidden'
                                    }}
                                  >
                                    {finding.findingTitle}
                                  </Typography>
                                </Box>
                              )}
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  lineHeight: 1.6,
                                  fontSize: '0.875rem',
                                  flex: 1,
                                  display: '-webkit-box',
                                  WebkitLineClamp: 8,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden'
                                }}
                              >
                                {finding.findingDescription}
                              </Typography>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Market Performance Dashboard */}
            {marketIdNum && (
              <Box sx={{ mb: 4 }}>
                <MarketPerformanceDashboard
                  marketId={marketIdNum}
                  marketName={marketDetails.name}
                  marketCountry={marketDetails.country?.name}
                />
              </Box>
            )}
          </>
        )}
      </Box>
    </Container>
  );
};

export default MarketPage;